import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { Customer } from '@jwp/ott-common/types/account';
import type { Subscription } from '@jwp/ott-common/types/subscription';
import { cleengFormatPrice, formatLocalizedDate } from '@jwp/ott-common/src/utils/formatting';

import Button from '../Button/Button';
import FormFeedback from '../FormFeedback/FormFeedback';
import { useTheme } from '../../../../../ThemeContext';
import CancelIcon from '../../../../theme/assets/icons/cancel_confirm.svg';

import styles from './RenewSubscriptionForm.module.scss';

type Props = {
  subscription: Subscription;
  customer: Customer;
  error: string | null;
  submitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

const RenewSubscriptionForm: React.FC<Props> = ({ subscription, customer, error, submitting, onConfirm, onClose }: Props) => {
  const { t, i18n } = useTranslation('account');
  const { universalStyles } = useTheme();
  const date = <span className={styles.values}>{`${formatLocalizedDate(new Date(subscription.expiresAt * 1000), i18n.language)}`}</span>;
  const price = <span className={styles.values}>{`${cleengFormatPrice(subscription.nextPaymentPrice, subscription.nextPaymentCurrency, customer.country)}`}</span>;
  return (
    <div>
      {error ? <FormFeedback variant="error">{error}</FormFeedback> : null}
      <span className={styles.icon}>
        <img src={CancelIcon} />
      </span>
      <h2 className={styles.title} style={universalStyles.primaryTextColor}>
        {t('renew_subscription.renew_your_subscription')}
      </h2>
      <p className={styles.paragraph} style={{ color: universalStyles.carouselPaginationInactive.backgroundColor }}>
        {' '}
        <Trans
          i18nKey="renew_subscription.description"
          ns="account"
          values={{
            date: formatLocalizedDate(new Date(subscription.expiresAt * 1000), i18n.language),
            price: cleengFormatPrice(subscription.nextPaymentPrice, subscription.nextPaymentCurrency, customer.country),
          }}
          components={{
            1: <span className={styles.values} />,
            3: <span className={styles.values} />,
          }}
        />
      </p>
      <div className={styles.buttonBar}>
        <Button label={t('renew_subscription.no_thanks')} onClick={onClose} fullWidth size="exlarge" variant="signoutYes" />
        <Button
          className={styles.confirmButton}
          color="primary"
          variant="contained"
          label={t('renew_subscription.renew_subscription')}
          onClick={onConfirm}
          fullWidth
          disabled={submitting}
          type="submit"
          size="exlarge"
        />
      </div>
    </div>
  );
};

export default RenewSubscriptionForm;
